import React, { useCallback, useEffect, useRef, useState } from 'react';

// Eigener Player für „In Aktion“ (15.10.2026, Davids Wunsch): statt der Browser-Leiste (mit Herunterladen,
// Drei-Punkte-Menü, Bild-in-Bild) eine schlichte Leiste im Seitenlook. Kein Play-Knopf in der Mitte
// (David: „nicht geil“), Pause über die Leiste oder einen Klick aufs Bild. Startet weiter stumm und in
// Schleife. Leiste nur beim Drüberfahren oder in der Pause; auf dem Handy blendet ein Tippen sie ein.

const zeit = s => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60), r = Math.floor(s % 60);
  return m + ':' + String(r).padStart(2, '0');
};

const Icon = ({ d }) => <svg viewBox="0 0 24 24" aria-hidden="true">{d}</svg>;
const PLAY = <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" />;
const PAUSE = <><rect x="6.5" y="5" width="3.6" height="14" rx="1.2" fill="currentColor" /><rect x="13.9" y="5" width="3.6" height="14" rx="1.2" fill="currentColor" /></>;
const LAUT = <><path d="M4 9.5h3.2L12 5.5v13l-4.8-4H4z" fill="currentColor" /><path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></>;
const STUMM = <><path d="M4 9.5h3.2L12 5.5v13l-4.8-4H4z" fill="currentColor" /><path d="M16 9.5l5 5M21 9.5l-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></>;
const VOLL = <path d="M4.5 9V5.5a1 1 0 0 1 1-1H9M15 4.5h3.5a1 1 0 0 1 1 1V9M19.5 15v3.5a1 1 0 0 1-1 1H15M9 19.5H5.5a1 1 0 0 1-1-1V15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />;
const KLEIN = <path d="M9 4.5V8a1 1 0 0 1-1 1H4.5M19.5 9H16a1 1 0 0 1-1-1V4.5M15 19.5V16a1 1 0 0 1 1-1h3.5M4.5 15H8a1 1 0 0 1 1 1v3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />;

export default function DemoPlayer({ src, en, onBereit, onFehler }) {
  const box = useRef(null), video = useRef(null), spur = useRef(null), ausblenden = useRef(null);
  const [laeuft, setLaeuft] = useState(false);
  const [stumm, setStumm] = useState(true);
  const [stand, setStand] = useState(0);
  const [dauer, setDauer] = useState(0);
  const [aktiv, setAktiv] = useState(false);
  const [ziehen, setZiehen] = useState(false);
  const [voll, setVoll] = useState(false);
  const T = (de, e) => (en ? e : de);

  // Leiste kurz zeigen, dann wieder ausblenden (solange das Video läuft)
  const zeigen = useCallback(() => {
    setAktiv(true);
    clearTimeout(ausblenden.current);
    ausblenden.current = setTimeout(() => setAktiv(false), 2500);
  }, []);
  useEffect(() => () => clearTimeout(ausblenden.current), []);

  useEffect(() => {
    const weg = () => setVoll(document.fullscreenElement === box.current || document.webkitFullscreenElement === box.current);
    document.addEventListener('fullscreenchange', weg);
    document.addEventListener('webkitfullscreenchange', weg);
    return () => { document.removeEventListener('fullscreenchange', weg); document.removeEventListener('webkitfullscreenchange', weg); };
  }, []);

  const umschalten = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };
  const ton = () => {
    const v = video.current;
    if (!v) return;
    v.muted = !v.muted;
    // Ton an, aber Lautstärke stand auf 0 → hörbar machen
    if (!v.muted && v.volume === 0) v.volume = 1;
    setStumm(v.muted);
  };
  const vollbild = () => {
    const b = box.current, v = video.current;
    if (voll) { (document.exitFullscreen || document.webkitExitFullscreen)?.call(document); return; }
    if (b.requestFullscreen) b.requestFullscreen().catch(() => {});
    else if (b.webkitRequestFullscreen) b.webkitRequestFullscreen();
    // iPhone kann nur das Video selbst ins Vollbild holen (dann mit der Apple-Leiste)
    else if (v && v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  // Zeitleiste: klicken oder ziehen
  const springen = e => {
    const v = video.current, s = spur.current;
    if (!v || !s || !dauer) return;
    const r = s.getBoundingClientRect();
    const anteil = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    v.currentTime = anteil * dauer;
    setStand(anteil * dauer);
  };
  const ziehStart = e => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setZiehen(true);
    springen(e);
  };
  const ziehBewegen = e => { if (ziehen) springen(e); };
  const ziehEnde = e => { setZiehen(false); try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {} };

  // Klick aufs Bild: mit Maus Play/Pause; per Finger erst die Leiste zeigen, wenn sie aus ist
  const aufsBild = e => {
    if (e.pointerType === 'touch' && laeuft && !aktiv) { zeigen(); return; }
    umschalten();
    zeigen();
  };

  const taste = e => {
    if (e.target !== box.current) return;
    if (e.key === ' ' || e.key === 'k') { e.preventDefault(); umschalten(); zeigen(); }
    else if (e.key === 'm') ton();
    else if (e.key === 'f') vollbild();
  };

  const anteil = dauer ? (stand / dauer) * 100 : 0;
  const sichtbar = !laeuft || aktiv || ziehen;

  return (
    <div ref={box} tabIndex={0} onKeyDown={taste} onMouseMove={zeigen} onMouseLeave={() => { clearTimeout(ausblenden.current); setAktiv(false); }}
      className={'spieler' + (laeuft ? '' : ' pause') + (sichtbar ? ' leiste-an' : '') + (voll ? ' voll' : '')}>
      <video ref={video} src={src} muted loop playsInline preload="metadata" disablePictureInPicture
        onContextMenu={e => e.preventDefault()}
        onPointerUp={aufsBild}
        onLoadedMetadata={e => setDauer(e.currentTarget.duration)}
        onLoadedData={() => { onBereit && onBereit(); const v = video.current; if (v) v.play().catch(() => {}); }}
        onPlay={() => setLaeuft(true)} onPause={() => setLaeuft(false)}
        onTimeUpdate={e => { if (!ziehen) setStand(e.currentTarget.currentTime); }}
        onVolumeChange={e => setStumm(e.currentTarget.muted)}
        onError={() => onFehler && onFehler()} />

      <div className="sp-leiste" onPointerUp={e => e.stopPropagation()}>
        <button type="button" className="sp-knopf" onClick={umschalten} aria-label={laeuft ? T('Pause', 'Pause') : T('Abspielen', 'Play')}>
          <Icon d={laeuft ? PAUSE : PLAY} />
        </button>
        <div ref={spur} className={'sp-spur' + (ziehen ? ' zieht' : '')} role="slider" aria-label={T('Zeitleiste', 'Timeline')}
          aria-valuemin={0} aria-valuemax={Math.round(dauer)} aria-valuenow={Math.round(stand)} aria-valuetext={zeit(stand)}
          onPointerDown={ziehStart} onPointerMove={ziehBewegen} onPointerUp={ziehEnde} onPointerCancel={ziehEnde}>
          <i><b style={{ width: anteil + '%' }} /><em style={{ left: anteil + '%' }} /></i>
        </div>
        <span className="sp-zeit">{zeit(stand)} <s>/ {zeit(dauer)}</s></span>
        <button type="button" className="sp-knopf" onClick={ton} aria-label={stumm ? T('Ton an', 'Unmute') : T('Ton aus', 'Mute')}>
          <Icon d={stumm ? STUMM : LAUT} />
        </button>
        <button type="button" className="sp-knopf" onClick={vollbild} aria-label={voll ? T('Vollbild beenden', 'Exit full screen') : T('Vollbild', 'Full screen')}>
          <Icon d={voll ? KLEIN : VOLL} />
        </button>
      </div>
    </div>
  );
}
